import { GET_TOKEN_WITH_POSITION } from '../graphql/queries/tokens';

/**
 * Updates the Apollo cache for a specific token with position data
 * This allows us to update individual components without refetching the entire query
 */
export const updateTokenWithPositionCache = (cache, variables, updateData) => {
  try {
    // Read the existing cache data
    const existingData = cache.readQuery({
      query: GET_TOKEN_WITH_POSITION,
      variables: variables, // { coingeckoSlug, portfolioId }
    });

    if (!existingData?.tokenWithPosition) {
      console.warn('No existing tokenWithPosition data found in cache');
      return;
    }

    // Create updated data by merging existing data with updates
    const updatedData = {
      ...existingData,
      tokenWithPosition: {
        ...existingData.tokenWithPosition,
        ...updateData,
      },
    };

    // Write the updated data back to the cache
    cache.writeQuery({
      query: GET_TOKEN_WITH_POSITION,
      variables: variables,
      data: updatedData,
    });

    console.log('Successfully updated Apollo cache for tokenWithPosition');
  } catch (error) {
    console.warn('Failed to update Apollo cache:', error);
    
    // If cache update fails due to missing fields, try to evict and let Apollo refetch
    if (error.message && error.message.includes('Missing field')) {
      console.warn('Cache field mismatch detected, evicting cache entry to force refetch');
      try {
        cache.evict({ 
          fieldName: 'tokenWithPosition', 
          args: variables 
        });
        cache.gc();
      } catch (evictError) {
        console.warn('Failed to evict cache:', evictError);
      }
    }
  }
};

/**
 * Updates just the position part of the tokenWithPosition cache
 */
export const updatePositionInCache = (cache, variables, newPosition) => {
  updateTokenWithPositionCache(cache, variables, {
    position: newPosition,
  });
};

/**
 * Updates just the token part of the tokenWithPosition cache
 */
export const updateTokenInCache = (cache, variables, newToken) => {
  updateTokenWithPositionCache(cache, variables, {
    token: newToken,
  });
};

/**
 * Updates specific fields in the position data
 */
export const updatePositionFields = (cache, variables, positionUpdates) => {
  try {
    const existingData = cache.readQuery({
      query: GET_TOKEN_WITH_POSITION,
      variables: variables,
    });

    if (!existingData?.tokenWithPosition?.position) {
      console.warn('No existing position data found in cache');
      return;
    }

    const updatedPosition = {
      ...existingData.tokenWithPosition.position,
      ...positionUpdates,
    };

    updatePositionInCache(cache, variables, updatedPosition);
  } catch (error) {
    console.warn('Failed to update position fields in cache:', error);
  }
};

/**
 * Adds a new transaction to the position's transactions array and triggers position recalculation
 */
export const addTransactionToCache = (cache, variables, newTransaction) => {
  try {
    const existing = cache.readQuery({ query: GET_TOKEN_WITH_POSITION, variables });
    if (!existing?.tokenWithPosition?.position) return;
    const prevTxs = existing.tokenWithPosition.position.transactions || [];
    const updated = {
      ...existing,
      tokenWithPosition: {
        ...existing.tokenWithPosition,
        position: {
          ...existing.tokenWithPosition.position,
          transactions: [newTransaction, ...prevTxs],
        },
      },
    };
    cache.writeQuery({ query: GET_TOKEN_WITH_POSITION, variables, data: updated });

  } catch (error) {
    console.warn('Failed to handle transaction cache update:', error);
  }
};

/**
 * Updates an existing transaction in the position's transactions array and triggers position recalculation
 */
export const updateTransactionInCache = (cache, variables, transactionId, updatedTransaction) => {
  try {
    const existing = cache.readQuery({ query: GET_TOKEN_WITH_POSITION, variables });
    if (!existing?.tokenWithPosition?.position) return;
    const prevTxs = existing.tokenWithPosition.position.transactions || [];
    const newTxs = prevTxs.map(tx => (tx.id === transactionId ? { ...tx, ...updatedTransaction } : tx));
    const updated = {
      ...existing,
      tokenWithPosition: {
        ...existing.tokenWithPosition,
        position: {
          ...existing.tokenWithPosition.position,
          transactions: newTxs,
        },
      },
    };
    cache.writeQuery({ query: GET_TOKEN_WITH_POSITION, variables, data: updated });

  } catch (error) {
    console.warn('Failed to handle transaction cache update:', error);
  }
};

/**
 * Removes a transaction from the position's transactions array and triggers position recalculation
 */
export const removeTransactionFromCache = (cache, variables, transactionId) => {
  try {
    const existing = cache.readQuery({ query: GET_TOKEN_WITH_POSITION, variables });
    if (!existing?.tokenWithPosition?.position) return;
    const prevTxs = existing.tokenWithPosition.position.transactions || [];
    const newTxs = prevTxs.filter(tx => tx.id !== transactionId);
    const updated = {
      ...existing,
      tokenWithPosition: {
        ...existing.tokenWithPosition,
        position: {
          ...existing.tokenWithPosition.position,
          transactions: newTxs,
        },
      },
    };
    cache.writeQuery({ query: GET_TOKEN_WITH_POSITION, variables, data: updated });

  } catch (error) {
    console.warn('Failed to handle transaction cache update:', error);
  }
};

/**
 * Updates the exit strategy in the position data
 */
export const updateExitStrategyInCache = (cache, variables, newExitStrategy) => {
  updatePositionFields(cache, variables, {
    exitStrategy: newExitStrategy,
  });
};

/**
 * Evicts position-related calculated fields to force refetch of position data
 * This is necessary when transactions change because position data is calculated server-side
 */
// Removed unused evictPositionCalculations to satisfy linter